import { useCallback, useEffect, useRef, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";

const ENDPOINT = "/admin/settings/client-booking";

/**
 * Client self-service policy (contract: backend/booking_policy.py — gates the public
 * `/api/bookings/manage/*` endpoints, never the practitioner's own
 * `/api/admin/bookings/{id}/reschedule`).
 *
 * `policy` shape: { enabled: bool, min_notice_hours: int (0..720) }. GET on mount;
 * `save()` PUTs the full document. Same shape as useNotificationSettings.js —
 * deliberately kept as a separate hook/endpoint rather than folded into it: this one
 * gates a CLIENT capability, notifications gate a practitioner INBOX preference, and
 * booking_policy.py is explicit that its sub-document must stay off the legacy
 * whitelist POST (`/admin/settings`) that useNotificationSettings' sibling avoids too.
 */
export default function useClientBookingPolicy() {
  const t = useT("admin");
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(ENDPOINT);
        if (!cancelled) setPolicy(data);
      } catch (e) {
        if (cancelled) return;
        const msg = extractApiError(e, t("avail.error"));
        setError(msg);
        toast.error(msg);
      }
    })();
    return () => {
      cancelled = true;
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [t]);

  const setField = useCallback((field, value) => {
    setPolicy((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const save = useCallback(async () => {
    if (!policy || busy) return;
    setBusy(true);
    setError("");
    setSaved(false);
    const payload = {
      enabled: !!policy.enabled,
      min_notice_hours: Number(policy.min_notice_hours),
    };
    try {
      const res = await api.put(ENDPOINT, payload);
      setPolicy(res?.data ?? payload);
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      // The only 422 this form can produce is min_notice_hours outside its
      // Field(ge=0, le=...) range in booking_policy.ClientBookingPolicy.
      const msg =
        e?.response?.status === 422
          ? t("settings.clientBooking.invalidNotice")
          : extractApiError(e, t("avail.error"));
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [policy, busy, t]);

  return { policy, setField, save, busy, error, saved };
}
